import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Popconfirm,
  Tag,
  Image,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  ReloadOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { Switch } from 'antd';
import { petCategoriesService, PetCategory } from '../services/petCategoriesService';
import { getImageUrl } from '../config/api';
import type { UploadProps } from 'antd';

const { Title } = Typography;

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<PetCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<PetCategory | null>(null);
  const [form] = Form.useForm();
  const [blockModalVisible, setBlockModalVisible] = useState(false);
  const [blockingCategory, setBlockingCategory] = useState<PetCategory | null>(null);
  const [blockForm] = Form.useForm();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await petCategoriesService.getCategories(true); // include inactive so admins can manage disabled categories
      setCategories(response.data?.categories || response.data || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (category: PetCategory) => {
    setEditingCategory(category);
    form.setFieldsValue(category);
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingCategory) {
        await petCategoriesService.updateCategory(editingCategory._id, values);
        message.success('Category updated successfully');
      } else {
        await petCategoriesService.createCategory(values);
        message.success('Category created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      fetchCategories();
    } catch (error: any) {
      message.error(error.message || 'Failed to save category');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await petCategoriesService.toggleCategoryStatus(id);
      message.success(`Category ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchCategories();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle category status');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await petCategoriesService.deleteCategory(id);
      message.success('Category deleted successfully');
      fetchCategories();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete category');
    }
  };

  const handleBlock = (category: PetCategory) => {
    setBlockingCategory(category);
    blockForm.resetFields();
    if (category.isBlocked) {
      // Unblocking - no reason needed
      handleToggleBlock(category._id);
    } else {
      // Blocking - show modal for reason
      setBlockModalVisible(true);
    }
  };

  const handleToggleBlock = async (id: string, blockReason?: string) => {
    try {
      await petCategoriesService.toggleCategoryBlock(id, blockReason);
      message.success(`Category ${blockingCategory?.isBlocked ? 'unblocked' : 'blocked'} successfully`);
      setBlockModalVisible(false);
      blockForm.resetFields();
      setBlockingCategory(null);
      fetchCategories();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle category block');
    }
  };

  const handleBlockSubmit = async (values: { blockReason?: string }) => {
    if (blockingCategory) {
      await handleToggleBlock(blockingCategory._id, values.blockReason);
    }
  };

  const uploadProps: UploadProps = {
    name: 'image',
    action: `${process.env.REACT_APP_API_URL || 'http://localhost:3008/api'}/upload`,
    headers: {
      authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    },
    onChange(info) {
      if (info.file.status === 'done') {
        message.success(`${info.file.name} file uploaded successfully`);
        form.setFieldsValue({ image: info.file.response?.data?.url });
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (image: string) => (
        <Image
          src={getImageUrl(image || '')}
          alt="Category"
          width={50}
          height={50}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="https://via.placeholder.com/50"
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Breeds',
      dataIndex: 'breedCount',
      key: 'breedCount',
      render: (count: number) => count || 0,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean, record: PetCategory) => (
        <Space>
          <Switch
            checked={isActive}
            onChange={() => handleToggleStatus(record._id, isActive)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
          <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
        </Space>
      ),
    },
    {
      title: 'Blocked',
      dataIndex: 'isBlocked',
      key: 'isBlocked',
      width: 120,
      render: (isBlocked: boolean, record: PetCategory) => (
        <Space>
          {isBlocked ? (
            <Tag color="red" icon={<StopOutlined />}>
              Blocked
            </Tag>
          ) : (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Allowed
            </Tag>
          )}
          {record.blockReason && (
            <span style={{ fontSize: '12px', color: '#666' }} title={record.blockReason}>
              {record.blockReason.substring(0, 30)}...
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: PetCategory) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={record.isBlocked ? <CheckCircleOutlined /> : <StopOutlined />}
            danger={!record.isBlocked}
            onClick={() => handleBlock(record)}
          >
            {record.isBlocked ? 'Unblock' : 'Block'}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this category?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>Pet Categories</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchCategories}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add Category
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={categories}
          rowKey="_id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="image" label="Image">
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
            <Input type="checkbox" /> Active
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Block Category for Illegal Animal Sales"
        open={blockModalVisible}
        onCancel={() => {
          setBlockModalVisible(false);
          blockForm.resetFields();
          setBlockingCategory(null);
        }}
        onOk={() => blockForm.submit()}
        okText="Block Category"
        okButtonProps={{ danger: true }}
      >
        <Form form={blockForm} layout="vertical" onFinish={handleBlockSubmit}>
          <Form.Item
            name="blockReason"
            label="Block Reason"
            rules={[{ required: true, message: 'Please provide a reason for blocking this category' }]}
            tooltip="This reason will be shown to users if they try to create posts in this category"
          >
            <Input.TextArea
              rows={4}
              placeholder="e.g., This category is blocked for illegal animal sales in most jurisdictions"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Categories;

